/*
 * Purpose: Persist Mission-generated artifacts to Postgres (retention SSOT)
 * Last modified: 2026-06-28 | Agent
 * Completeness: 95
 */
import { apiUrl } from '@/lib/apiBase';
import type { MissionGeneratedKind } from '@/lib/missionRetentionPolicy';
import { slimPayloadForRetention } from '@/lib/missionPayloadSlim';
import { VAULT_FIELD_MARKER } from '@/services/mission-knowledge/types';

function sanitizePayload(payload: unknown): Record<string, unknown> {
  if (!payload || typeof payload !== 'object') return {};
  try {
    const raw = JSON.stringify(payload);
    const redacted = raw.replace(
      /"(api[_-]?key|secret|token|password|authorization)"\s*:\s*"[^"]*"/gi,
      '"$1":"[redacted]"'
    );
    const parsed = JSON.parse(redacted) as Record<string, unknown>;
    return stripVaultMarkers(parsed);
  } catch {
    return { note: 'payload serialization failed' };
  }
}

function stripVaultMarkers(obj: Record<string, unknown>): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(obj)) {
    if (typeof v === 'string' && v.startsWith(VAULT_FIELD_MARKER)) {
      out[k] = '[vault — retained marker only]';
    } else if (v && typeof v === 'object' && !Array.isArray(v)) {
      out[k] = stripVaultMarkers(v as Record<string, unknown>);
    } else {
      out[k] = v;
    }
  }
  return out;
}

export async function persistMissionGenerated(
  missionSessionId: string,
  kind: MissionGeneratedKind,
  payload: unknown,
  options?: {
    rawOutcome?: string;
    structuredAnswers?: Record<string, string>;
  }
): Promise<{ ok: boolean; retained?: boolean; message?: string }> {
  if (!missionSessionId || !kind) return { ok: true, retained: false };

  const slimmed = slimPayloadForRetention(kind, sanitizePayload(payload));

  try {
    const res = await fetch(apiUrl(`/api/missions/${encodeURIComponent(missionSessionId)}/generated`), {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        kind,
        payload: slimmed,
        rawOutcome: options?.rawOutcome ?? null,
        structuredAnswers: options?.structuredAnswers ?? {},
      }),
    });

    const body = (await res.json()) as { ok?: boolean; error?: string; message?: string };

    if (res.status === 503) {
      return { ok: true, retained: false, message: body.message ?? 'Mission DB not configured' };
    }

    if (!res.ok) {
      return { ok: false, retained: false, message: body.error ?? `Retain failed (${res.status})` };
    }

    return { ok: true, retained: true };
  } catch (e) {
    return {
      ok: false,
      retained: false,
      message: e instanceof Error ? e.message : 'Retain failed',
    };
  }
}

/** Fire-and-forget retention — local cache remains; Postgres is durable when configured. */
export function retainMissionGeneratedAsync(
  missionSessionId: string | undefined,
  kind: MissionGeneratedKind,
  payload: unknown,
  options?: {
    rawOutcome?: string;
    structuredAnswers?: Record<string, string>;
  }
): void {
  if (!missionSessionId) return;
  void persistMissionGenerated(missionSessionId, kind, payload, options);
}

export function retainMissionSessionPolicyAsync(
  missionSessionId: string | undefined,
  policy: {
    rawOutcome?: string;
    structuredAnswers?: Record<string, string>;
    automationConsent?: unknown;
    riskApprovals?: unknown;
  }
): void {
  if (!missionSessionId) return;
  void persistMissionSessionPolicy(missionSessionId, policy);
}

export async function persistMissionSessionPolicy(
  missionSessionId: string,
  policy: {
    rawOutcome?: string;
    structuredAnswers?: Record<string, string>;
    automationConsent?: unknown;
    riskApprovals?: unknown;
  }
): Promise<{ ok: boolean; message?: string }> {
  if (!missionSessionId) return { ok: true };

  try {
    const res = await fetch(apiUrl(`/api/missions/${encodeURIComponent(missionSessionId)}/policy`), {
      method: 'PUT',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(policy),
    });

    if (res.status === 503) return { ok: true, message: 'Mission DB not configured' };

    const body = (await res.json()) as { error?: string };
    if (!res.ok) return { ok: false, message: body.error ?? `Policy sync failed (${res.status})` };
    return { ok: true };
  } catch (e) {
    return { ok: false, message: e instanceof Error ? e.message : 'Policy sync failed' };
  }
}
